import { useCallback, useEffect, useState } from "react";
import { useAdminFetch } from "../lib/admin-api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";

interface SignalGroup {
  score: number;
  [k: string]: unknown;
}
interface Signals {
  name?: SignalGroup;
  owner?: SignalGroup;
  address?: SignalGroup;
  digital?: SignalGroup;
  license_network?: SignalGroup;
  member_count?: number;
}
interface Candidate {
  id: string;
  distributor_license_number: string;
  chain_id: string;
  score: string;
  signals: Signals;
  status: string;
  created_at: string;
  resolved_by: string | null;
  dismissed_reason: string | null;
  dist_dba: string | null;
  dist_legal: string | null;
  dist_owner: string | null;
  dist_email: string | null;
  dist_phone: string | null;
  dist_city: string | null;
  dist_street: string | null;
  dist_license_type: string | null;
  dist_current_chain_id: string | null;
  chain_display_name: string | null;
  chain_canonical_root: string | null;
  chain_status: string | null;
  chain_member_count: number | null;
}

type Filter = "pending" | "attached" | "dismissed" | "stale";
const FILTERS: Filter[] = ["pending", "attached", "dismissed", "stale"];

function scoreBadge(score: number): string {
  if (score >= 0.9) return "bg-green-100 text-green-800 border-green-200";
  if (score >= 0.7) return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-gray-100 text-gray-700 border-gray-200";
}

function pct(n: unknown): string {
  const v = typeof n === "number" ? n : Number(n);
  return Number.isFinite(v) ? v.toFixed(2) : "—";
}

export default function DistributorBridge() {
  const adminFetch = useAdminFetch();
  const toast = useToast();

  const [filter, setFilter] = useState<Filter>("pending");
  const [rows, setRows] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [busyRow, setBusyRow] = useState<string | null>(null);

  const [dismissTarget, setDismissTarget] = useState<Candidate | null>(null);
  const [dismissReason, setDismissReason] = useState("");
  const [dismissing, setDismissing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminFetch<{ candidates: Candidate[] }>(
        `/api/admin/distributor-bridge?status=${filter}&limit=200`
      );
      setRows(r.candidates || []);
    } catch (err) {
      toast.push(`Failed to load candidates: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [adminFetch, toast, filter]);

  useEffect(() => {
    load();
  }, [load]);

  const attach = async (c: Candidate) => {
    if (
      !window.confirm(
        `Attach distributor ${c.distributor_license_number} (${c.dist_dba || c.dist_legal || "?"}) ` +
          `to chain "${c.chain_display_name}"?`
      )
    )
      return;
    setBusyRow(c.id);
    try {
      await adminFetch(`/api/admin/distributor-bridge/${encodeURIComponent(c.id)}/attach`, {
        method: "POST",
      });
      toast.push(`Attached to ${c.chain_display_name}`, "success");
      await load();
    } catch (err) {
      toast.push(`Attach failed: ${(err as Error).message}`, "error");
    } finally {
      setBusyRow(null);
    }
  };

  const confirmDismiss = async () => {
    if (!dismissTarget) return;
    setDismissing(true);
    try {
      await adminFetch(`/api/admin/distributor-bridge/${encodeURIComponent(dismissTarget.id)}/dismiss`, {
        method: "POST",
        body: JSON.stringify({ reason: dismissReason.trim() || undefined }),
      });
      toast.push("Dismissed", "success");
      setDismissTarget(null);
      setDismissReason("");
      await load();
    } catch (err) {
      toast.push(`Dismiss failed: ${(err as Error).message}`, "error");
    } finally {
      setDismissing(false);
    }
  };

  const unattach = async (c: Candidate) => {
    if (!window.confirm(`Unattach ${c.distributor_license_number} from its chain? This reopens it for review.`))
      return;
    setBusyRow(c.id);
    try {
      await adminFetch(
        `/api/admin/distributor-bridge/${encodeURIComponent(c.distributor_license_number)}/unattach`,
        { method: "POST" }
      );
      toast.push("Unattached", "success");
      await load();
    } catch (err) {
      toast.push(`Unattach failed: ${(err as Error).message}`, "error");
    } finally {
      setBusyRow(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Distributor → Parent Bridge</h2>
        <p className="text-sm text-gray-600 mt-1">
          C11 distributor licenses scored against approved retail chains by the multi-signal bridge
          scorer. Auto-attach fires at ≥0.90 (unambiguous) once the sweep is live; 0.70–0.90 land
          here for review.
        </p>
      </div>

      <div className="flex items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-medium border capitalize transition-colors ${
              filter === f
                ? "bg-accent-500 text-white border-accent-500"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-600">No {filter} candidates.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Distributor</th>
                <th className="text-left px-4 py-2 font-medium">Proposed parent chain</th>
                <th className="text-left px-4 py-2 font-medium">Score</th>
                <th className="text-left px-4 py-2 font-medium">Signals</th>
                <th className="text-left px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const score = Number(c.score);
                const s = c.signals || {};
                const isOpen = expanded[c.id];
                return (
                  <tr key={c.id} className="border-t border-gray-100 align-top">
                    <td className="px-4 py-2 text-gray-900 max-w-[18rem]">
                      <a
                        href={`#/review/enrich/${encodeURIComponent(c.distributor_license_number)}`}
                        className="font-mono text-xs text-accent-700 hover:underline"
                      >
                        {c.distributor_license_number}
                      </a>
                      <div className="text-gray-900">{c.dist_dba || "—"}</div>
                      {c.dist_legal && c.dist_legal !== c.dist_dba && (
                        <div className="text-xs text-gray-500">legal: {c.dist_legal}</div>
                      )}
                      {c.dist_owner && <div className="text-xs text-gray-500">owner: {c.dist_owner}</div>}
                    </td>
                    <td className="px-4 py-2 text-gray-900 max-w-[16rem]">
                      <a
                        href={`#/chains/${encodeURIComponent(c.chain_id)}`}
                        className="text-accent-700 hover:underline font-medium"
                      >
                        {c.chain_display_name || "—"}
                      </a>
                      <div className="text-xs text-gray-500 font-mono">{c.chain_canonical_root}</div>
                      <div className="text-xs text-gray-400">
                        {c.chain_member_count ?? "—"} members · {c.chain_status}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${scoreBadge(score)}`}
                      >
                        {score.toFixed(3)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600 max-w-[18rem]">
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>name {pct(s.name?.score)}</span>
                        <span>owner {pct(s.owner?.score)}</span>
                        <span>digital {pct(s.digital?.score)}</span>
                        <span>addr {pct(s.address?.score)}</span>
                        <span>lic {pct(s.license_network?.score)}</span>
                      </div>
                      <button
                        className="mt-1 text-accent-700 hover:underline"
                        onClick={() => setExpanded((p) => ({ ...p, [c.id]: !isOpen }))}
                      >
                        {isOpen ? "hide raw" : "raw signals"}
                      </button>
                      {isOpen && (
                        <pre className="mt-1 max-h-48 overflow-auto rounded bg-gray-50 border border-gray-200 p-2 text-[11px] leading-tight">
                          {JSON.stringify(s, null, 2)}
                        </pre>
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {c.status === "pending" ? (
                        <div className="flex items-center gap-2">
                          <Button variant="primary" size="sm" disabled={busyRow === c.id} onClick={() => attach(c)}>
                            {busyRow === c.id ? "…" : "Attach"}
                          </Button>
                          <Button variant="secondary" size="sm" disabled={busyRow === c.id} onClick={() => setDismissTarget(c)}>
                            Dismiss
                          </Button>
                        </div>
                      ) : c.status === "attached" ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-green-700">attached{c.resolved_by ? ` · ${c.resolved_by}` : ""}</span>
                          <Button variant="secondary" size="sm" disabled={busyRow === c.id} onClick={() => unattach(c)}>
                            {busyRow === c.id ? "…" : "Unattach"}
                          </Button>
                        </div>
                      ) : c.status === "dismissed" ? (
                        <span className="text-xs text-gray-400" title={c.dismissed_reason || ""}>
                          dismissed{c.dismissed_reason ? `: ${c.dismissed_reason}` : ""}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">stale</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {dismissTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => !dismissing && setDismissTarget(null)}
        >
          <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900">Dismiss candidate</h3>
            <p className="text-sm text-gray-600 mt-1">
              Dismiss the match between <span className="font-medium">{dismissTarget.dist_dba || dismissTarget.distributor_license_number}</span>{" "}
              and <span className="font-medium">{dismissTarget.chain_display_name}</span>. No chain_id is set.
            </p>
            <label className="mt-4 block text-xs text-gray-500">
              Reason (optional)
              <textarea
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                rows={3}
                className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDismissTarget(null)} disabled={dismissing}>
                Cancel
              </Button>
              <Button variant="primary" onClick={confirmDismiss} disabled={dismissing}>
                {dismissing ? "Dismissing…" : "Dismiss"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
